/**
 * GoogleConnectionsCard — gestione centralizzata del collegamento Google
 * (Calendar + Tasks condividono la stessa connessione OAuth).
 *
 * Permette di: collegare un nuovo account, aggiungerne altri, ricollegare un
 * account scaduto e scollegare singoli account o tutti. Usato nella sezione
 * "Collegamenti" dell'admin.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar as CalIcon, ListTodo, Plus, Unplug, X, Loader2,
  CheckCircle2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const ACCOUNT_COLORS = [
  { bg: "bg-rose-100", ring: "ring-rose-400", text: "text-rose-800" },
  { bg: "bg-amber-100", ring: "ring-amber-400", text: "text-amber-800" },
  { bg: "bg-emerald-100", ring: "ring-emerald-400", text: "text-emerald-800" },
  { bg: "bg-sky-100", ring: "ring-sky-400", text: "text-sky-800" },
  { bg: "bg-violet-100", ring: "ring-violet-400", text: "text-violet-800" },
  { bg: "bg-pink-100", ring: "ring-pink-400", text: "text-pink-800" },
  { bg: "bg-teal-100", ring: "ring-teal-400", text: "text-teal-800" },
  { bg: "bg-indigo-100", ring: "ring-indigo-400", text: "text-indigo-800" },
];
const accountStyle = (email) => {
  if (!email) return ACCOUNT_COLORS[0];
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  return ACCOUNT_COLORS[hash % ACCOUNT_COLORS.length];
};
const initialFor = (email) => (email || "?").trim().charAt(0).toUpperCase() || "?";

export default function GoogleConnectionsCard() {
  const [status, setStatus] = useState(null); // {configured, connected}
  const [accounts, setAccounts] = useState([]); // [{google_email, ...}]
  const [tasksInfo, setTasksInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/admin/calendar/status");
      setStatus(s.data);
      if (s.data?.connected) {
        const [acc, gt] = await Promise.all([
          api.get("/admin/calendar/accounts").catch(() => ({ data: { accounts: [] } })),
          api.get("/admin/google-tasks").catch(() => ({ data: {} })),
        ]);
        setAccounts(acc.data.accounts || []);
        setTasksInfo(gt.data || null);
      } else {
        setAccounts([]);
        setTasksInfo(null);
      }
    } catch {
      /* silente */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/oauth/calendar/authorize");
      window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Impossibile avviare il collegamento Google");
      setConnecting(false);
    }
  };

  const disconnectOne = async (email) => {
    if (!window.confirm(`Scollegare l'account ${email}? Calendar e Tasks di questo account non saranno più disponibili.`)) return;
    try {
      await api.delete("/admin/calendar/disconnect", { params: { google_email: email } });
      toast.success(`${email} scollegato`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore disconnessione");
    }
  };

  const disconnectAll = async () => {
    if (!window.confirm("Scollegare TUTTI gli account Google (Calendar + Tasks)?")) return;
    try {
      await api.delete("/admin/calendar/disconnect");
      toast.success("Tutti gli account Google scollegati");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore disconnessione");
    }
  };

  const taskByEmail = {};
  (tasksInfo?.accounts || []).forEach((a) => { if (a?.google_email) taskByEmail[a.google_email] = a; });

  const connected = !!status?.connected;

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="google-connections-card">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md ${connected ? "bg-emerald-50" : "bg-slate-100"}`}>
            <CalIcon size={14} className={connected ? "text-emerald-700" : "text-slate-500"} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold flex items-center gap-1.5">
              Google
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`}
                title={connected ? "Collegato" : "Non collegato"}
              />
            </div>
            <h2 className="font-display text-base text-ink leading-tight flex items-center gap-2">
              Calendar &amp; Tasks
              <ListTodo size={13} className="text-slate-400" />
            </h2>
          </div>
          {connected && (
            <button
              type="button"
              onClick={load}
              className="ml-auto p-1.5 text-slate-400 hover:text-ink rounded"
              title="Aggiorna stato"
              data-testid="google-refresh"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {loading && !status ? (
          <div className="text-xs text-slate-400 flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Verifica collegamento…
          </div>
        ) : !status?.configured ? (
          <div className="flex items-start gap-2 text-xs text-slate-600" data-testid="google-not-configured">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-slate-700 font-medium">Non configurato</div>
              <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                Manca <code className="bg-slate-100 px-1">GOOGLE_CLIENT_ID/SECRET</code> nel server.
              </div>
            </div>
          </div>
        ) : !connected ? (
          <>
            <div className="flex items-start gap-2 text-xs text-slate-600">
              <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-slate-700 font-medium">Nessun account collegato</div>
                <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                  Collega uno o più account Google per sincronizzare Calendar e Tasks nella Home.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={connect}
              disabled={connecting}
              className="w-full bg-ink hover:bg-slate-800 text-white text-[10px] tracking-wider uppercase font-semibold px-2.5 py-2 rounded flex items-center justify-center gap-1.5 transition disabled:opacity-50"
              data-testid="google-connect"
            >
              <Plus size={12} /> {connecting ? "Reindirizzamento…" : "Collega account Google"}
            </button>
          </>
        ) : (
          <>
            <ul className="space-y-2" data-testid="google-accounts-list">
              {accounts.map((a) => {
                const email = a.google_email;
                const s = accountStyle(email);
                const t = taskByEmail[email];
                const needsReconnect = t?.needs_reconnect;
                const apiDisabled = t?.api_disabled;
                return (
                  <li
                    key={email}
                    className="flex items-center gap-2 bg-cream2/60 border border-ink/10 rounded-sm px-2.5 py-2"
                    data-testid={`google-account-${email}`}
                  >
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold shrink-0 ${s.bg} ${s.text} ring-1 ${s.ring}`}>
                      {initialFor(email)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-ink truncate" title={email}>{email}</div>
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {needsReconnect ? (
                          <span className="text-[9px] tracking-[0.1em] uppercase bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                            Tasks da ricollegare
                          </span>
                        ) : apiDisabled ? (
                          <span className="text-[9px] tracking-[0.1em] uppercase bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-sm">
                            API Tasks disattivata
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.1em] uppercase text-emerald-700">
                            <CheckCircle2 size={10} /> Calendar &amp; Tasks attivi
                          </span>
                        )}
                      </div>
                    </div>
                    {needsReconnect ? (
                      <button
                        type="button"
                        onClick={connect}
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded transition"
                        data-testid={`google-reconnect-${email}`}
                      >
                        <RefreshCw size={11} /> Ricollega
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => disconnectOne(email)}
                      className="shrink-0 p-1 text-slate-400 hover:text-rose-600 transition"
                      title="Scollega questo account"
                      data-testid={`google-disconnect-${email}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>

            {(tasksInfo?.accounts || []).some((a) => a?.api_disabled) && (
              <div className="bg-rose-50 border border-rose-200 rounded-sm p-2.5 text-[11px] text-rose-900" data-testid="google-tasks-api-disabled">
                <strong>API Google Tasks non abilitata</strong> nel progetto Google Cloud. Abilitala su{" "}
                <a href="https://console.cloud.google.com/apis/library/tasks.googleapis.com" target="_blank" rel="noreferrer" className="underline font-semibold">console.cloud.google.com → Tasks API</a>, poi aggiorna.
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={connect}
                disabled={connecting}
                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-dashed border-ink/30 hover:border-gold text-ink hover:text-gold text-[10px] tracking-wider uppercase font-semibold px-2.5 py-2 rounded transition disabled:opacity-50"
                data-testid="google-add-account"
              >
                <Plus size={12} /> {connecting ? "Reindirizzamento…" : "Aggiungi account"}
              </button>
              {accounts.length > 1 && (
                <button
                  type="button"
                  onClick={disconnectAll}
                  className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-semibold bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200 hover:border-rose-200 px-2.5 py-2 rounded transition"
                  data-testid="google-disconnect-all"
                >
                  <Unplug size={12} /> Scollega tutti
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
